import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trophy, Medal, Crown, ArrowLeft, User, Flame, Star, Map } from 'lucide-react';
import { DECK_INFO, DECK_TYPES } from '../mock/gameData';

const Leaderboard = ({ onBack, user }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [deckLeaderboard, setDeckLeaderboard] = useState([]);
  const [journeyLeaderboard, setJourneyLeaderboard] = useState([]);
  const [myScores, setMyScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGlobalLeaderboard();
    if (user) {
      fetchMyScores();
    }
  }, [user]);

  useEffect(() => {
    if (selectedDeck) {
      fetchDeckLeaderboard(selectedDeck);
    }
  }, [selectedDeck]);

  const getAuthHeaders = () => {
    const sessionToken = localStorage.getItem('session_token');
    const headers = {};
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    return headers;
  };

  const fetchGlobalLeaderboard = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/leaderboard/global`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setGlobalLeaderboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch global leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeckLeaderboard = async (deckType) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/leaderboard/deck/${deckType}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setDeckLeaderboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch deck leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyScores = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/leaderboard/my-scores`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMyScores(data);
      }
    } catch (error) {
      console.error('Failed to fetch my scores:', error);
    }
  };

  const fetchJourneyLeaderboard = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/journey/leaderboard`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        // Add rank to each entry
        const rankedData = data.map((entry, index) => ({
          ...entry,
          rank: index + 1
        }));
        setJourneyLeaderboard(rankedData);
      }
    } catch (error) {
      console.error('Failed to fetch journey leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-gray-400 font-bold">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-600/30 to-yellow-400/30 border-yellow-500/50';
    if (rank === 2) return 'bg-gradient-to-r from-gray-500/30 to-gray-300/30 border-gray-400/50';
    if (rank === 3) return 'bg-gradient-to-r from-amber-700/30 to-amber-500/30 border-amber-500/50';
    return 'bg-white/5 border-white/10';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const decksForSelector = Object.entries(DECK_INFO).map(([key, value]) => ({
    type: key,
    name: value.name,
    icon: value.icon,
  }));

  const renderLeaderboardEntries = (entries) => {
    if (entries.length === 0) {
      return (
        <div className="text-center py-12">
          <Trophy className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No scores yet!</p>
          <p className="text-gray-500">Be the first to make the leaderboard!</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div
            key={index}
            className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:scale-[1.02] ${getRankBg(entry.rank)}`}
          >
            <div className="flex-shrink-0">
              {getRankIcon(entry.rank)}
            </div>
            
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white truncate">
                  {entry.nickname}
                </span>
                {entry.is_winner && (
                  <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                    <Star className="w-3 h-3 mr-1" />
                    Winner
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{entry.deck_name}</span>
                <span>•</span>
                <span>{formatDate(entry.created_at)}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <div className="flex items-center gap-1 text-green-400">
                  <Trophy className="w-4 h-4" />
                  <span className="font-bold text-lg">{entry.score}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-orange-400">
                  <Flame className="w-4 h-4" />
                  <span className="font-medium">{entry.win_streak}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderJourneyLeaderboard = (entries) => {
    if (entries.length === 0) {
      return (
        <div className="text-center py-12">
          <Map className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No Journey attempts yet!</p>
          <p className="text-gray-500">Be the first to complete The Journey!</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {entries.map((entry, index) => (
          <div
            key={index}
            className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:scale-[1.02] ${getRankBg(entry.rank)}`}
          >
            <div className="flex-shrink-0">
              {getRankIcon(entry.rank)}
            </div>
            
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-white truncate">
                  {entry.nickname || 'Anonymous'}
                </span>
                {entry.is_journey_complete && (
                  <Badge className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border-yellow-500/30">
                    <Crown className="w-3 h-3 mr-1" />
                    Legendary
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{formatDate(entry.best_run_date)}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="text-right">
                <div className="flex items-center gap-1 text-cyan-400">
                  <Map className="w-5 h-5" />
                  <span className="font-bold text-2xl">{entry.decks_completed}</span>
                </div>
                <span className="text-xs text-gray-500">Decks Passed</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={onBack}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Trophy className="w-8 h-8 text-yellow-400" />
            Leaderboard
          </h1>
          
          <div className="w-24" /> {/* Spacer for centering */}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => { setActiveTab('global'); setSelectedDeck(null); }}
            className={`flex-1 ${activeTab === 'global' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            <Trophy className="w-4 h-4 mr-2" />
            Global
          </Button>
          <Button
            onClick={() => { setActiveTab('journey'); fetchJourneyLeaderboard(); }}
            className={`flex-1 ${activeTab === 'journey' ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            <Map className="w-4 h-4 mr-2" />
            Journey
          </Button>
          <Button
            onClick={() => setActiveTab('by-deck')}
            className={`flex-1 ${activeTab === 'by-deck' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
          >
            <Medal className="w-4 h-4 mr-2" />
            By Deck
          </Button>
          {user && (
            <Button
              onClick={() => setActiveTab('my-scores')}
              className={`flex-1 ${activeTab === 'my-scores' ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
            >
              <User className="w-4 h-4 mr-2" />
              My Scores
            </Button>
          )}
        </div>

        {/* Deck Selector for By Deck tab */}
        {activeTab === 'by-deck' && (
          <div className="mb-6">
            <p className="text-gray-300 mb-3">Select a deck:</p>
            <div className="flex flex-wrap gap-2">
              {decksForSelector.map((deck) => (
                <Button
                  key={deck.type}
                  onClick={() => setSelectedDeck(deck.type)}
                  size="sm"
                  className={`${
                    selectedDeck === deck.type
                      ? 'bg-purple-600 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  <span className="mr-1">{deck.icon}</span>
                  {deck.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading leaderboard...</p>
            </div>
          ) : (
            <>
              {activeTab === 'global' && renderLeaderboardEntries(globalLeaderboard)}
              {activeTab === 'journey' && renderJourneyLeaderboard(journeyLeaderboard)}
              {activeTab === 'by-deck' && (
                selectedDeck ? renderLeaderboardEntries(deckLeaderboard) : (
                  <div className="text-center py-12">
                    <Medal className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">Select a deck to view its leaderboard</p>
                  </div>
                )
              )}
              {activeTab === 'my-scores' && (
                myScores.length === 0 ? (
                  <div className="text-center py-12">
                    <User className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No scores yet!</p>
                    <p className="text-gray-500">Play a game to see your scores here!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {myScores.map((score, index) => (
                      <div
                        key={score.score_id || index}
                        className="flex items-center gap-4 p-4 rounded-xl border bg-white/5 border-white/10"
                      >
                        <div className="flex-grow">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{score.deck_name}</span>
                            {score.is_winner && (
                              <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                                <Star className="w-3 h-3 mr-1" />
                                Winner
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm text-gray-400">{formatDate(score.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-green-400">
                            <Trophy className="w-4 h-4" />
                            <span className="font-bold">{score.score}</span>
                          </div>
                          <div className="flex items-center gap-1 text-orange-400">
                            <Flame className="w-4 h-4" />
                            <span>{score.win_streak}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
